import { ca } from 'date-fns/locale'
import { get } from 'http'

type Stringable = string | number

export function endpoints() {
  return {
    spotInfo: (spotId: Stringable) => `${spotId}/webapp/spot_info`,
    inquiriesList: (spotId: Stringable) => `${spotId}/webapp/inquiries`,
    createInquiry: (spotId: Stringable) => `${spotId}/webapp/inquiries`,
    updateInquiry: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/webapp/put_inquiries/${inquiryId}`,
    deleteInquiry: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/webapp/inquiries/${inquiryId}`,
    getInquiry: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/webapp/inquiries/${inquiryId}`,
    getProposal: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/webapp/proposal_for_inquiry/${inquiryId}`,
    createProposal: (spotId: Stringable) =>
      `${spotId}/webapp/create_proposal_for_inquiry`,
    editProposal: (spotId: Stringable, proposalPayment: Stringable) =>
      `${spotId}/webapp/update_proposal_for_inquiry/${proposalPayment}`,
    createProposalAttachment: (spotId: Stringable) =>
      `${spotId}/webapp/attachments`,
    refundPayment: (spotId: Stringable, paymentID: Stringable) =>
      `${spotId}/webapp/refund_payment/${paymentID}`,
    capturePayment: (spotId: Stringable, paymentID: Stringable) =>
      `${spotId}/webapp/capture_payment/${paymentID}`,
    getInquiryComments: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/webapp/inquiries/${inquiryId}/inquiry_comments/`,
    createInquiryComment: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/webapp/inquiries/${inquiryId}/inquiry_comments/`,
    getStandardInquiryAutoResponses: (
      spotId: Stringable,
      type: string,
      inquiryId: Stringable,
    ) =>
      `${spotId}/webapp/standard_inquiry_responses/${type}?inquiry_id=${inquiryId}`,
    getAutoResponseComment: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/webapp/autoresponse_comment/${inquiryId}`,
    cancelAutoResponse: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/webapp/cancel_autoresponse/${inquiryId}`,
    markCommentAsSeen: (
      spotId: Stringable,
      inquiryId: Stringable,
      inquiryCommentsId: Stringable,
    ) =>
      `${spotId}/webapp/inquiries/${inquiryId}/inquiry_comments/${inquiryCommentsId}/see`,
    markCommentAsUnseen: (
      spotId: Stringable,
      inquiryId: Stringable,
      inquiryCommentsId: Stringable,
    ) =>
      `${spotId}/webapp/inquiries/${inquiryId}/inquiry_comments/${inquiryCommentsId}/unsee`,
    getInquiryAttachments: (spotId: Stringable) =>
      `${spotId}/webapp/attachments`,
    getReusableAttachments: (spotId: Stringable) =>
      `${spotId}/webapp/reusable_attachments_list`,
    createInquiryAttachment: (spotId: Stringable) =>
      `${spotId}/webapp/create_attachment_for_inquiry_comment`,
    deleteAttachment: (spotId: Stringable, attachment_id: Stringable) =>
      `${spotId}/webapp/attachments/${attachment_id}`,
    cancelDelivery: (spotId: Stringable, orderId: Stringable) =>
      `${spotId}/webapp/cancel_delivery/${orderId}`,
    getDelivery: (spotId: Stringable, orderId: Stringable) =>
      `${spotId}/webapp/delivery/${orderId}`,
    toggleSpotNotifications: (spotId: Stringable) =>
      `${spotId}/webapp/toggle_notifications`,
    deleteCookies: (spotId: Stringable) => `${spotId}/webapp/cookies`,
    mixpanel: (spotId: Stringable) => `${spotId}/webapp`,
    getAppNotifications: (spotId: Stringable) =>
      `${spotId}/webapp/app_notification_settings`,
    pandadocTokens: (spotId: Stringable) => `${spotId}/webapp/pandadoc_token`,
    customerFeedback: (spotId: Stringable) => `${spotId}/webapp/customer_feedback`,
    putCustomerFeedback: (spotId: Stringable) => `${spotId}/webapp/put_customer_feedback`,
  } as const
}

export function calendarEndpoints() {
  return {
    getCalendarEntries: (spotId: Stringable) => `${spotId}/calendar_v2/entries`,
    getCalendarPromotions: (spotId: Stringable) =>
      `${spotId}/calendar_v2/promotions`,
    getCalendarBlockedDays: (spotId: Stringable) =>
      `${spotId}/calendar_v2/blockers`,
    getCalendarSpotInfo: (spotId: Stringable) => `${spotId}/calendar_v2/info`,
    getCalendarFlyerCaptions: (spotId: Stringable, id: Stringable) =>
      `${spotId}/calendar_v2/caption_idea/${id}`,
    getCalendarPostWorthyCaptions: (spotId: Stringable, id: Stringable) =>
      `${spotId}/calendar_v2/pwi_post_caption_idea/${id}`,
    getCalendarGalleryPhotos: (spotId: Stringable) =>
      `${spotId}/calendar_v2/gallery`,
    calendarUploadGalleryPhoto: (spotId: Stringable) =>
      `${spotId}/calendar_v2/gallery`,
    calendarApprovePost: (spotId: Stringable, id: Stringable) =>
      `${spotId}/calendar_v2/approve/${id}`,
    calendarPostNow: (spotId: Stringable, id: Stringable) =>
      `${spotId}/calendar_v2/post_now/${id}`,
    calendarEditPost: (spotId: Stringable, id: Stringable) =>
      `${spotId}/calendar_v2/item/${id}`,
    calendarDeleteEntry: (spotId: Stringable, id: Stringable) =>
      `${spotId}/calendar_v2/item/${id}`,
    calendarChangeTextFragment: (spotId: Stringable, id: Stringable) =>
      `${spotId}/calendar_v2/change_text_fragment/${id}`,
    calendarMixPanel: (spotId: Stringable) => `${spotId}/calendar_v2/mixpanel`,
    getCalendarActiveHolidayPromotions: (spotId: Stringable) =>
      `${spotId}/calendar_v2/active_holiday_promotions`,
    updateCalendarSpotInfo: (spotId: Stringable) =>
      `${spotId}/calendar_v2/info`,
    viewPost: (spotId: Stringable, id: Stringable) =>
      `${spotId}/calendar_v2/view_post/${id}`,
    calendarMediaEditorQueue: (spotId: Stringable) => `${spotId}/media_editor`,
    calendarMediaEditorRetryContentEdit: (spotId: Stringable, id: Stringable) =>
      `${spotId}/media_editor/retry_content_edit/${id}`,
    calendarMediaEditorCancelContentEdit: (
      spotId: Stringable,
      id: Stringable,
    ) => `${spotId}/media_editor/cancel_content_edit/${id}`,
  } as const
}

export function desktopEndpoints() {
  return {
    getInquiryComments: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/inquiry_manager/inquiries/${inquiryId}/inquiry_comments/`,
    getInquiry: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/inquiry_manager/inquiries/${inquiryId}`,
    markCommentAsSeen: (
      spotId: Stringable,
      inquiryId: Stringable,
      inquiryCommentsId: Stringable,
    ) =>
      `${spotId}/inquiry_manager/inquiries/${inquiryId}/inquiry_comments/${inquiryCommentsId}/see`,
    markCommentAsUnseen: (
      spotId: Stringable,
      inquiryId: Stringable,
      inquiryCommentsId: Stringable,
    ) =>
      `${spotId}/inquiry_manager/inquiries/${inquiryId}/inquiry_comments/${inquiryCommentsId}/unsee`,
    getStandardInquiryAutoResponses: (
      spotId: Stringable,
      type: string,
      inquiryId: Stringable,
    ) =>
      `${spotId}/inquiry_manager/standard_inquiry_responses/${type}?inquiry_id=${inquiryId}`,
    getAutoResponseComment: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/inquiry_manager/autoresponse_comment/${inquiryId}`,
    cancelAutoResponse: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/inquiry_manager/cancel_autoresponse/${inquiryId}`,
    pandadocTokens: (spotId: Stringable) =>
      `${spotId}/inquiry_manager/pandadoc_token`,
    getInquiryAttachments: (spotId: Stringable) =>
      `${spotId}/inquiry_manager/attachments`,
    getReusableAttachments: (spotId: Stringable) =>
      `${spotId}/inquiry_manager/reusable_attachments_list`,
    createInquiryAttachment: (spotId: Stringable) =>
      `${spotId}/inquiry_manager/create_attachment_for_inquiry_comment`,
    getAiResponses: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/inquiry_manager/ai_responses/${inquiryId}`,
    createInquiryComment: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/inquiry_manager/inquiries/${inquiryId}/inquiry_comments/`,
    updateInquiry: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/inquiry_manager/put_inquiries/${inquiryId}`,
    deleteInquiry: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/inquiry_manager/inquiries/${inquiryId}`,
    createProposal: (spotId: Stringable) =>
      `${spotId}/inquiry_manager/create_proposal_for_inquiry`,
    editProposal: (spotId: Stringable, proposalPayment: Stringable) =>
      `${spotId}/inquiry_manager/update_proposal_for_inquiry/${proposalPayment}`,
    getProposal: (spotId: Stringable, inquiryId: Stringable) =>
      `${spotId}/inquiry_manager/proposal_for_inquiry/${inquiryId}`,
    createProposalAttachment: (spotId: Stringable) =>
      `${spotId}/inquiry_manager/attachments`,
    enableAutoConfirm: (spotId: Stringable) =>
      `${spotId}/inquiry_manager/enable_auto_confirm`,
    enableReservationReminder: (spotId: Stringable) =>
      `${spotId}/inquiry_manager/enable_reservation_reminder`,
    inquiriesList: (spotId: Stringable) => `${spotId}/inquiry_manager/inquiries`,
    seeInquiries: (spotId: Stringable) => `${spotId}/inquiry_manager/see_inquiries`,
    unseeInquiries: (spotId: Stringable) => `${spotId}/inquiry_manager/unsee_inquiries`,
    archiveInquiries: (spotId: Stringable) => `${spotId}/inquiry_manager/archive_inquiries`,
    mixpanel: (spotId: Stringable) => `${spotId}/inquiry_manager`,
    }
}
